import { Button, Card, Heading } from '@snsw/react-component-library';
import { GelTheme } from 'components/GelTheme';
import { SummaryCardStatusRow } from 'components/SummaryCard/SummaryCardStatusRow';
import React, { PropsWithChildren, useState } from 'react';
import styled, { css } from 'styled-components/macro';

export interface SummaryCardProps {
  id: string;
  title?: string;
  status?: {
    text: string;
    variant?: 'neutral' | 'error' | 'success' | 'warning' | 'info';
  };

  defaultCollapsed?: boolean;
  disableCollapse?: boolean;

  actionProps?: {
    text: string;
    type?: 'button' | 'submit' | 'reset';
    variant?: 'primary' | 'secondary' | 'tertiary';
    disabled?: boolean;
    onClick: () => void | Promise<void>;
  };
}

const AutoHeightCard = styled(Card)`
  height: auto;
  padding: ${GelTheme.spacing(1.5)};
`;

const CardTitle = styled(Heading)`
  &&& {
    margin-top: 0;
    margin-bottom: 0;
  }
  padding-top: ${GelTheme.spacing(0.5)};
  padding-bottom: ${GelTheme.spacing(1)};
`;

const ButtonContainer = styled.div`
  margin-top: ${GelTheme.spacing(2.5)};
`;

const CardContent = styled.div<{ collapsed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${GelTheme.spacing(1)};
  overflow: hidden;
  transition: max-height 400ms ease-in;
  // max-height: 100vh;

  ${(props) =>
    props.collapsed &&
    css`
      max-height: 0;
    `}
`;

/**
 * A collapsible card which displays summary content with a status icon and an optional call to action.
 */
export const SummaryCard = ({
  id,
  title,
  status,
  defaultCollapsed,
  disableCollapse,
  actionProps,
  children,
}: PropsWithChildren<SummaryCardProps>) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const handleToggleCollapsed = () => setCollapsed((prev) => !prev);

  return (
    <AutoHeightCard>
      <SummaryCardStatusRow
        status={status}
        disableCollapse={disableCollapse}
        collapsed={collapsed}
        onToggleCollapsed={handleToggleCollapsed}
        contentId={id}
      />

      {title && <CardTitle level={2}>{title}</CardTitle>}
      <CardContent id={id} collapsed={collapsed}>
        {children}
      </CardContent>

      {actionProps && (
        <ButtonContainer>
          <Button
            type={actionProps.type}
            variant={actionProps.variant ?? 'secondary'}
            disabled={actionProps.disabled}
            onClick={actionProps.onClick}
          >
            {actionProps.text}
          </Button>
        </ButtonContainer>
      )}
    </AutoHeightCard>
  );
};

SummaryCard.defaultProps = {
  defaultCollapsed: false,
  disableCollapse: false,
};
